import { createGetRequest } from 'app/selectors';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalDialog } from 'components/ModalDialog';
import { TableLoader } from 'components/TableLoader';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { WorkOrderActKc2OtherAggregationData, WorkOrderActKc2Data } from 'gql/graphql';
import { WorkOrderStatusEnum, ProjectMethodologyEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { CreateOther } from 'pages/Project/Common/Contract/OtherCreate';
import { useConstructionTypesListItems } from 'pages/Project/Common/hooks/useConstructionTypesListItems';
import { Checkbox, Loader, Table, IconAlert, Informer, TextField } from 'uikit';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import type { TextFieldPropValue } from '@consta/uikit/TextField';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { addOtherToActKC2, workOrderOthersForActKC2 } from './addOrderOtherQueries';
import type { TCheckedIdsWithSeasonsCost } from './addOtherHelpers';
import {
    checkOtherHaveEmptySeasonCostForAct,
    createFilterChangeHandler,
    prepareAddOrderOtherToAktKC2Ids,
} from './addOtherHelpers';
import { getAddOthersBaseColumns } from './addOthersBaseColumns';
import type { IOthersData } from './types';
import { useAddOtherFilter } from './useAddOtherFilter';

import Styles from './AddOrderOther.module.css';

export const AddOrderOtherToAktKC2 = ({
    onConfirm,
    onCancel,
    constructionTypeId,
    workOrderObjectId,
}: {
    onConfirm: () => void;
    onCancel: () => void;
    constructionTypeId: number;
    workOrderObjectId: number;
}) => {
    const { id: projectId, orderId } = useParams();
    const { getConstructionTypeName } = useConstructionTypesListItems();
    const { projectData } = useProjectData(projectId);
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const dispatch = useDispatch();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [checkedIdsWithSeasonCosts, setCheckedIdsWithSeasonCosts] = useState<TCheckedIdsWithSeasonsCost>({});
    const [createdOtherId, setCreatedOtherId] = useState<number | null>(null);
    const [isConfirmModalOpened, setIsConfirmModalOpened] = useState(false);
    const [valueNote, setValueNote] = useState<Record<string, string>>({});

    const requestSelector = useMemo(() => createGetRequest('workOrderOthers'), []);
    const { data: workOrderOtherData } = useSelector(requestSelector);

    const seasonIdsWithActs = useMemo(
        () => workOrderOtherData?.workOrderOtherPriceList?.actsKC2?.map((act: WorkOrderActKc2Data) => act.seasonId),
        [workOrderOtherData?.workOrderOtherPriceList?.actsKC2],
    );

    const workOrderOthersVariables = useMemo(
        () => ({
            workOrderObjectId,
        }),
        [workOrderObjectId],
    );

    const contractOtherCostIds = useMemo(
        () => prepareAddOrderOtherToAktKC2Ids(checkedIdsWithSeasonCosts),
        [checkedIdsWithSeasonCosts],
    );

    const isOtherNotChecked = useMemo(() => contractOtherCostIds.length === 0, [contractOtherCostIds.length]);

    const handleChangesValueNote = useCallback(
        (rowId: number) =>
            ({ value }: { value: TextFieldPropValue }) => {
                setValueNote((ids) => ({ ...ids, [rowId]: value }));
            },
        [],
    );

    const otherInputs = useMemo(
        () =>
            contractOtherCostIds.map((otherCostId) => ({
                otherCostId,
                rationale: valueNote[otherCostId] || '',
            })),
        [contractOtherCostIds, valueNote],
    );

    const addOtherToActKC2Variables = useMemo(
        () => ({
            workOrderObjectId,
            otherInputs,
        }),
        [otherInputs, workOrderObjectId],
    );

    const [workOrderOthersForActKC2Data, workOrderOthersForActKC2Status, , workOrderOthersForActKC2Request] =
        useRequest({
            requestId: 'workOrderOthersForActKC2',
            query: workOrderOthersForActKC2,
            withEffect: false,
        });

    const [addOtherToActKC2Data, addOtherToActKC2Status, addOtherToActKC2Error, addOtherToActKC2Request] = useRequest({
        requestId: 'addOtherToActKC2',
        query: addOtherToActKC2,
        withEffect: false,
    });
    const [makeAddOrderOtherReset] = useResetStoreData({ requestId: 'addOtherToActKC2' });
    const isAddInProgress = useMemo(() => addOtherToActKC2Status === RequestStatus.FETCHING, [addOtherToActKC2Status]);
    const isTableLoading = useMemo(
        () => workOrderOthersForActKC2Status === RequestStatus.FETCHING,
        [workOrderOthersForActKC2Status],
    );
    const isTableLoadingSuccess = useMemo(
        () => workOrderOthersForActKC2Status === RequestStatus.SUCCESS,
        [workOrderOthersForActKC2Status],
    );

    useEffect(() => {
        if (addOtherToActKC2Data?.uerAddOtherCostToActKC2?.result === true && !isAddInProgress) {
            makeAddOrderOtherReset(undefined);
            onConfirm?.();
        }
    }, [addOtherToActKC2Data?.uerAddOtherCostToActKC2?.result, isAddInProgress, makeAddOrderOtherReset, onConfirm]);

    useEffect(() => {
        dispatch(workOrderOthersForActKC2Request(workOrderOthersVariables));
    }, [dispatch, workOrderOthersForActKC2Request, workOrderOthersVariables]);

    const tableRows = useMemo(
        () =>
            workOrderOthersForActKC2Data?.uerWorkOrderOthersForActKC2.map((item) => ({
                id: item.contractOtherCostId,
                ...item,
            })) || [],
        [workOrderOthersForActKC2Data?.uerWorkOrderOthersForActKC2],
    );

    const handleChangeCheckbox = useCallback(
        (row: IOthersData) =>
            ({ checked }: { checked: boolean }) => {
                if (checked) {
                    setCheckedIdsWithSeasonCosts((prev) => ({
                        ...prev,
                        [row.id]: row.limits?.map((limit) => ({
                            [limit.season.id]: limit.costLimit,
                        })),
                    }));
                } else {
                    setCheckedIdsWithSeasonCosts((prev) => ({
                        ...prev,
                        [row.id]: null,
                    }));
                }
            },
        [],
    );

    useEffect(() => {
        if (isTableLoadingSuccess && createdOtherId) {
            const row = tableRows.find(({ id }: { id: number }) => id === createdOtherId);

            if (row) {
                handleChangeCheckbox(row as any)({ checked: true });
                setCreatedOtherId(null);
            }
        }
    }, [createdOtherId, handleChangeCheckbox, isTableLoadingSuccess, tableRows]);

    const tableFilters = useAddOtherFilter(tableRows);
    const [visibleTableData, setVisibleTableData] = useState<WorkOrderActKc2OtherAggregationData[]>([]);

    useEffect(() => {
        if (tableRows.length) {
            setVisibleTableData(tableRows);
        }
    }, [tableRows]);

    const handleFilterChange = useMemo(() => createFilterChangeHandler(tableRows, setVisibleTableData), [tableRows]);
    const addOthersBaseColumns = useMemo(
        () =>
            getAddOthersBaseColumns(
                projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0,
            ),
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding],
    );

    const tableColumns = useMemo(
        () => [
            {
                title: '',
                accessor: 'checkbox',
                width: 40,
                withoutPadding: true,
                renderCell: (row: IOthersData) => (
                    <Checkbox
                        checked={!!checkedIdsWithSeasonCosts[row.id as number]}
                        onChange={handleChangeCheckbox(row)}
                        style={{ margin: '16px 12px' }}
                    />
                ),
            },
            ...addOthersBaseColumns,
            {
                title: 'Примечание',
                accessor: 'note',
                width: 200,
                renderCell: (row: IOthersData) => (
                    <TextField
                        value={valueNote[row.id]}
                        onChange={handleChangesValueNote(row.id as number)}
                        disabled={!checkedIdsWithSeasonCosts[row.id as number]}
                        placeholder="Введите примечание"
                        size="s"
                    />
                ),
            },
        ],
        [addOthersBaseColumns, checkedIdsWithSeasonCosts, handleChangeCheckbox, handleChangesValueNote, valueNote],
    );

    const handleSave = useCallback(() => {
        dispatch(addOtherToActKC2Request(addOtherToActKC2Variables));
        setIsSubmitted(true);

        sendGpnAnalyticsEvent({
            componentId: 'rim_act KC_add to act KC other button_pressed',
            projectId,
            orderId: Number(orderId),
            orderStatus: WorkOrderStatusEnum.InProgress,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        });
    }, [
        addOtherToActKC2Request,
        addOtherToActKC2Variables,
        dispatch,
        orderId,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
    ]);

    const saveCheckHandler = useCallback(() => {
        if (
            checkOtherHaveEmptySeasonCostForAct({
                checkedIdsWithSeasonCosts,
                seasonIdsWithActs,
            })
        ) {
            setIsConfirmModalOpened(true);
        } else {
            handleSave();
        }
    }, [checkedIdsWithSeasonCosts, handleSave, seasonIdsWithActs]);

    const closeConfirmModal = useCallback(() => {
        setIsConfirmModalOpened(false);
    }, []);

    const handleCancel = useCallback(() => {
        onCancel();
    }, [onCancel]);

    const reloadWorkOrderOtherTable = useCallback(
        (id: number) => {
            setCreatedOtherId(id);
            dispatch(workOrderOthersForActKC2Request(workOrderOthersVariables));
        },
        [dispatch, workOrderOthersForActKC2Request, workOrderOthersVariables],
    );

    const handleCloseModalError = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const handleCreateReport = useCallback(() => {
        ExportCSVReport(addOtherToActKC2Data?.uerAddOtherCostToActKC2?.validation);
    }, [addOtherToActKC2Data?.uerAddOtherCostToActKC2?.validation]);

    return (
        <ModalDialog
            title={`Лимиты прочих затрат для вида строительства "${getConstructionTypeName(constructionTypeId)}"`}
            isOpen
            onConfirm={saveCheckHandler}
            onClose={handleCancel}
            btnConfirmLabel="Добавить в Акт"
            withTitleCloseButton
            leftPanel={
                <CreateOther
                    currentConstructionTypeId={constructionTypeId}
                    reloadHandler={reloadWorkOrderOtherTable}
                    methodology={ProjectMethodologyEnum.Uer}
                    workOrderId={Number(orderId)}
                />
            }
            style={{ maxWidth: 1240 }}
            isCommit={isAddInProgress}
            btnConfirmDisabled={isOtherNotChecked}
            isOnClickOutsideDisable
        >
            <div style={{ margin: '20px' }}>
                <Informer
                    status="system"
                    label={`Выбранные прочие затраты  будут применены к объекту `}
                    icon={IconAlert}
                />
            </div>
            <div className={Styles.otherTable}>
                {isTableLoading && <Loader />}

                {!isTableLoading && (
                    <Table
                        columns={tableColumns}
                        rows={visibleTableData}
                        size="s"
                        stickyHeader
                        emptyRowsPlaceholder={<TableLoader requestStatus={workOrderOthersForActKC2Status} />}
                        verticalAlign="center"
                        borderBetweenRows
                        filters={tableFilters}
                        onFiltersUpdated={handleFilterChange}
                    />
                )}
            </div>
            <ErrorHandlingModal
                titleText="Добавить прочие"
                isSubmitted={isSubmitted}
                statusRequest={addOtherToActKC2Status}
                dataRequest={addOtherToActKC2Data?.uerAddOtherCostToActKC2}
                errorsRequest={addOtherToActKC2Error}
                handleCloseModalError={handleCloseModalError}
                handleCreateReport={handleCreateReport}
            />
            {isConfirmModalOpened && (
                <ConfirmationDialog
                    isOpened
                    onSubmit={handleSave}
                    onClose={closeConfirmModal}
                    textHeader="Добавление прочих в акт кс-2"
                    textConfirmation="Вы собираетесь добавить в Акт прочие затраты, по которым отсутствует лимит по договору для выбранного сезона.
После добавления прочих затрат в Акт потребуется перераспределение лимитов по виду строительства в рамках работы с Изм. к Наряд-Заказу"
                    confirmButtonText="Добавить в Акт"
                    cancelButtonText="Отменить"
                />
            )}
        </ModalDialog>
    );
};

AddOrderOtherToAktKC2.displayName = 'AddOrderOtherToAktKC2';
